import { combineReducers } from 'redux';
import {
  FETCH_ALL_SUCCESS,
  FETCH_ONE_SUCCESS,
  SET_QUEUE,
  SET_LAST_UPDATE,
  START_UPDATING,
  FINISH_UPDATING,
  UPDATE_ITEM,
  RESTORE,
  RESTORE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  CREATE_SUCCESS,
  RESTORE_SUCCESS,
  CREATE,
  CREATE_FAILURE,
  ADD_TEMP_UPDATE,
  REMOVE_TEMP_UPDATE,
  ADD_TEMP_DELETE,
  REMOVE_TEMP_DELETE,
  SHOW_REPEAT_POPUP,
  HIDE_REPEAT_POPUP,
  UPDATE_SUCCESS,
  FETCH_ALL,
  SET_TASK_ERROR,
} from './constants';
import { allExceptType } from './selectors';
import { identifierForTask } from './helpers';

// Re-point child rows when their parent's id changes underneath them.
//
// A task's id is not stable. Editing a single occurrence of a recurring task sends
// `confirm: 'this'`, and the backend answers with a freshly minted exception row
// carrying a BRAND-NEW id (see childReorderPayload). A newly created task likewise
// swaps its temp id for the server's on CREATE_SUCCESS.
//
// Children reference their parent by that id via `parent_id`, and allForDayAndType
// groups strictly on it, so a parent that changes id silently orphans its children:
// they stay in the store but stop rendering until the next fetch re-reads them with
// the new id. Dragging a recurring parent made its subtasks vanish until refresh for
// exactly this reason (ARTFLT-1571).
//
// Only the id that identified that one parent is remapped, so this cannot touch
// another group: an occurrence id belongs to a single occurrence, and a temp id to a
// single client-created row.
export const reparentChildren = (tasks, previousId, nextId) => {
  if (!previousId || !nextId || previousId === nextId) return tasks;
  let changed = false;
  const next = tasks.map(task => {
    if (task.parent_id !== previousId) return task;
    changed = true;
    return { ...task, parent_id: nextId };
  });
  return changed ? next : tasks;
};

// `confirm` is a REQUEST parameter -- which occurrences the server should apply a
// change to -- not task state. The reducer needs it to pick a branch, and then it has
// to go: every `updateTask({ ...task, ... })` call site takes its scope from the row it
// spreads (`confirm: data.confirm || 'all'`), so a value left on the row silently
// re-scopes the NEXT edit of that task.
//
// That is how reordering a subtask blanked the page. A completion stores
// `confirm: 'this'` on the row; the reorder spreads the row, and childReorderPayload's
// non-recurring path strips `start` precisely because it expects the family-wide branch
// to restore it from the store. Inheriting 'this' sent it to the single-occurrence
// branch instead, which appends the payload verbatim -- so a row with no `start` landed
// in the store and formattedTasks threw on `start.substring`, taking the whole tree
// down with it (ARTFLT-1576).
//
// Returns the input unchanged when there is nothing to strip; never mutates.
export const withoutRequestScope = task => {
  if (!task || !('confirm' in task)) return task;
  const next = { ...task };
  delete next.confirm;
  return next;
};

const taskCollectionInitialState = {
  tasks: [],
  requests: {},
  fetching: {},
};

const taskCollectionReducer = (state = taskCollectionInitialState, action) => {
  switch (action.type) {
    case FETCH_ALL:
      console.log(FETCH_ALL, action.taskType, action.stamp);
      return {
        ...state,
        requests: { ...state.requests, [action.taskType]: action.stamp },
        fetching: { ...state.fetching, [action.taskType]: true },
      };
    case FETCH_ALL_SUCCESS: {
      const currentStamp = state.requests[action.taskType];
      if (currentStamp && currentStamp > action.stamp) {
        console.log(
          FETCH_ALL_SUCCESS,
          `stale request ${action.taskType} ${currentStamp} > ${action.stamp}`
        );
        return state;
      }
      console.log(
        FETCH_ALL_SUCCESS,
        `updating ${action.taskType} ${currentStamp} <= ${action.stamp}`
      );
      return {
        ...state,
        tasks: [...allExceptType(state.tasks, action.taskType), ...action.data],
        fetching: { ...state.fetching, [action.taskType]: false },
      };
    }
    case FETCH_ONE_SUCCESS:
      return {
        ...state,
        tasks: [...state.tasks.filter(({ id }) => id !== action.data.id), action.data],
      };
    case UPDATE_SUCCESS:
    case UPDATE_ITEM: {
      const { confirm, id } = action.data;

      // `tempId` is client-only (see createTask), so the server row arriving on
      // UPDATE_SUCCESS never carries one. Every branch below replaces the stored task
      // wholesale, which drops it — and list renderers key on `tempId || id`, so a
      // freshly created row's key would flip from its tempId to its real id on the
      // first successful update, remounting the row and dropping focus while the user
      // is still typing. Carry it across each replacement.
      const keepTempId = (next, existing) =>
        existing?.tempId ? { ...next, tempId: existing.tempId } : next;

      // A payload that OMITS a key must not erase it from the store. Every branch
      // below replaces the stored task wholesale, so an absent `parent_id` silently
      // un-parented the row: `allForDayAndType` groups children by `parent_id` and
      // TaskList renders indentation from it, so the row jumped out to top level on
      // the optimistic dispatch and snapped back an instant later when the server
      // response arrived carrying the real value.
      //
      // sectionMovePayloads deliberately does not send `parent_id` — the rendered
      // row's copy can name a per-occurrence exception parent, which the server
      // would persist, detaching the child from the whole series.
      //
      // Keyed on PRESENCE, not truthiness: an explicit `parent_id: null` is an
      // outdent and must still apply, and the server response always carries the
      // authoritative value. Mirrors the backend, which only fills attributes that
      // are actually present in the request.
      const keepStoredParent = (next, existing) =>
        'parent_id' in next || !existing?.parent_id
          ? next
          : { ...next, parent_id: existing.parent_id };

      if (confirm?.startsWith('this')) {
        const existing = state.tasks.find(
          task => identifierForTask(task) === identifierForTask(action.data)
        );
        // The response to a single-occurrence edit is a new exception row with a new
        // id, so any children of the row it replaces have to follow it.
        const remaining = reparentChildren(
          state.tasks.filter(task => identifierForTask(task) !== identifierForTask(action.data)),
          existing?.id,
          action.data.id
        );
        return {
          ...state,
          tasks: [...remaining, withoutRequestScope(keepTempId(action.data, existing))],
        };
      }

      if (confirm?.startsWith('following')) {
        return {
          ...state,
          tasks: state.tasks.map(task => {
            if (task.id !== id || identifierForTask(task) < identifierForTask(action.data)) {
              return task;
            }
            return withoutRequestScope(
              keepTempId(
                { ...action.data, start: task.start, recurrence_id: task.recurrence_id },
                task
              )
            );
          }),
        };
      }

      // Family-wide fallback, reached by `confirm: 'all'` and by any payload that names
      // no scope at all. It matches on `id` ALONE, which does not identify a row: a
      // recurring task is one row per occurrence in the fetched range and a base
      // subtask is cloned onto each parent occurrence, every copy carrying the same
      // base `id`. That is correct for 'all' — a family-wide edit should reach every
      // occurrence — and silently destructive for anything that meant to touch one.
      //
      // Nothing should land here by omission. `updateTask` stamps `confirm: data.confirm
      // || 'all'` on everything it dispatches, and the two raw UPDATE_ITEM dispatches in
      // TaskItem's `toggle` — the only ones in the app that bypass it — now name their
      // own scope. They did not, and one checkbox click completed every visible
      // occurrence (ARTFLT-1576).
      return {
        ...state,
        tasks: state.tasks.map(task => {
          if (task.id !== id) {
            return task;
          }
          return withoutRequestScope(
            keepTempId(
              keepStoredParent(
                { ...action.data, start: task.start, recurrence_id: task.recurrence_id },
                task
              ),
              task
            )
          );
        }),
      };
    }
    case RESTORE_FAILURE:
    case DELETE: {
      const { confirm, ics } = action.data;
      if (confirm?.startsWith('this')) {
        return {
          ...state,
          tasks: state.tasks.filter(
            task => identifierForTask(task) !== identifierForTask(action.data)
          ),
        };
      }

      if (confirm?.startsWith('following')) {
        return {
          ...state,
          tasks: state.tasks.filter(
            task => task.ics !== ics || identifierForTask(task) < identifierForTask(action.data)
          ),
        };
      }

      return {
        ...state,
        tasks: state.tasks.filter(task => task.ics !== ics),
      };
    }
    case DELETE_SUCCESS: {
      return {
        ...state,
        tasks: state.tasks.map(task => {
          if (task.id === action.data.id) {
            return {
              ...task,
              repeat_rule: action.data.repeat_rule,
              updated_at: action.data.updated_at,
            };
          } else {
            return task;
          }
        }),
      };
    }
    case DELETE_FAILURE:
    case RESTORE:
    case CREATE:
      return {
        ...state,
        tasks: [...state.tasks, withoutRequestScope(action.data)],
      };
    case CREATE_SUCCESS:
      return {
        ...state,
        // Carry the temp id forward as `tempId`. The server's `id` replaces the temp
        // `id` here, so anything keyed on `id` would see the row as a brand-new task
        // and remount it, dropping focus while the user is still typing. `tempId`
        // survives the swap and gives list renderers a stable identity to key on.
        // Children added under this row while the create was still in flight point at
        // the temp id, so they move across with it.
        tasks: [
          ...reparentChildren(
            state.tasks.filter(({ id }) => id !== action.id),
            action.id,
            action.data.id
          ),
          withoutRequestScope({ ...action.data, tempId: action.id }),
        ],
      };
    case CREATE_FAILURE:
      return { ...state, tasks: [...state.tasks.filter(({ id }) => id !== action.id)] };
  }
  return state;
};

const updatesInitialState = {
  queue: [],
  lastUpdates: {},
  isUpdating: false,
  taskError: null,
};

const updatesReducer = (state = updatesInitialState, action) => {
  switch (action.type) {
    case START_UPDATING:
      return { ...state, isUpdating: true };
    case FINISH_UPDATING:
      return { ...state, isUpdating: false };
    case SET_QUEUE:
      return { ...state, queue: action.queue };

    case SET_LAST_UPDATE:
      return {
        ...state,
        lastUpdates: {
          ...state.lastUpdates,
          [action.original.id]: { updated_at: action.data.updated_at },
          [identifierForTask(action.data)]: action.data,
        },
      };

    case SET_TASK_ERROR:
      console.log(SET_TASK_ERROR, action.data);
      return { ...state, taskError: action.data };
  }
  return state;
};

const tempUpdatesReducer = (state = {}, action) => {
  switch (action.type) {
    case ADD_TEMP_UPDATE:
      return { ...state, [action.data.id]: action.data };
    case REMOVE_TEMP_UPDATE:
      return { ...state, [action.data.id]: null };
  }
  return state;
};

const tempDeletesReducer = (state = {}, action) => {
  switch (action.type) {
    case ADD_TEMP_DELETE:
      return { ...state, [action.data.id]: action.data };
    case REMOVE_TEMP_DELETE:
      return { ...state, [action.data.id]: null };
  }
  return state;
};

const repeatPopupInitialState = {
  show: false,
  repeatable: null,
  positionRect: null,
  onSave: null,
  onCancel: null,
};

const repeatPopupReducer = (state = repeatPopupInitialState, action) => {
  switch (action.type) {
    case SHOW_REPEAT_POPUP: {
      const { repeatable, positionRect, onSave, onCancel } = action;
      return { ...state, show: true, repeatable, positionRect, onSave, onCancel };
    }
    case HIDE_REPEAT_POPUP:
      return repeatPopupInitialState;
  }
  return state;
};

export default combineReducers({
  taskCollection: taskCollectionReducer,
  updates: updatesReducer,
  tempUpdates: tempUpdatesReducer,
  tempDeletes: tempDeletesReducer,
  repeatPopup: repeatPopupReducer,
});
